import type { Channel, Child, DonorStatus, Donor, Entity, EntityOf, EntityRef, EntityType, ErpGraph, Letter, Osp } from './contract'
import { CHANNELS, DONOR_STATUSES, ENTITY_TYPES } from './contract'

/* ERP · consultas puras sobre o grafo (cliente e servidor usam as mesmas). Tudo derivado do ErpGraph:
   ligações entre entidades, textos curtos e as respostas do painel da diretoria (perguntas da ontologia).
   createErpIndex(graph) monta os índices uma vez; as telas usam o objeto devolvido (useErp() em api.ts).
   Datas relativas a graph.today (nunca ao relógio). No live, o que a base não traz chega vazio ou null: os textos
   pulam o que falta em vez de inventar. */

export type Relation = EntityRef & { rel: string }
export type ChannelStat = { channel: Channel; total: number; active: number; canceled: number; guardians: number; retention: number; avgTicket: number; avgMonths: number }
export type ChurchKindStat = { kind: string; churches: number; donors: number; canceled: number; retention: number; avgTicket: number; avgMonths: number }
export type Overdue = { donor: Donor; months: number; amount: number; reason: string; since: string; days: number }
export type Exit = { donor: Donor; origin: string; stage: 'onboarding (90 dias)' | '1º ano' | 'após 1 ano'; months: number; reason: string; date: string }
export type LetterDue = { donor: Donor; letter: Letter }
export type OspRevenue = { osp: Osp; gross: number; ospShare: number; cfbShare: number; donors: number }
export type MonthSummary = {
    month: string
    /** Quantidades de faturas. */
    billed: number; paid: number; denied: number; overdue: number; open: number
    /** Valores (R$). */
    billedAmount: number; paidAmount: number; overdueAmount: number
    byMethod: Record<string, { count: number; paid: number; amount: number }>
}

export type ErpIndex = {
    graph: ErpGraph
    get<T extends EntityType>(type: T, id: string | null | undefined): EntityOf<T> | undefined
    all<T extends EntityType>(type: T): EntityOf<T>[]
    /** Ligações de uma entidade, sem repetir (rel = texto curto: 'origem', 'inscrição', 'apadrinha', 'veio daqui'…). */
    relations(e: Entity): Relation[]
    /** Uma linha curta (ex. igreja: 'Contagem/MG · pentecostal'). */
    metaOf(e: Entity): string
    /** 2 a 3 linhas para a prévia ao passar o mouse. */
    peekLines(e: Entity): string[]
    donorsFrom(type: EntityType, id: string): Donor[]
    donorsOfEvent(id: string): Donor[]
    donorsOfConsultor(id: string): Donor[]
    donorsOfOsp(id: string): Donor[]
    childrenOf(ospId: string): Child[]
    activeChildren(donor: Donor): Child[]
    channelStats(): ChannelStat[]
    churchKindStats(): ChurchKindStat[]
    overdue(): Overdue[]
    exits(): Exit[]
    near180(): Child[]
    lettersDue(): LetterDue[]
    revenueByOsp(month?: string): OspRevenue[]
    monthSummary(month?: string): MonthSummary
    countByStatus(): Record<DonorStatus, number>
}

// ---------- rótulos e formatação ----------

export const CHANNEL_LABELS: Record<Channel, string> = {
    live: 'Live Concert (igreja)',
    igreja: 'Indicação de igreja',
    empresa: 'Empresa',
    embaixador: 'Embaixador',
    site: 'Site',
    tele: 'Telemarketing',
}
export const DONOR_STATUS_LABELS: Record<DonorStatus, string> = { lead: 'Lead', pre: 'Pré-ativo', ativo: 'Ativo', inadimplente: 'Inadimplente', cancelado: 'Cancelado' }
export const CHILD_STATUS_LABELS: Record<Child['status'], string> = { apadrinhada: 'Apadrinhada', disponível: 'Disponível', reservada: 'Reservada', desativada: 'Desativada' }
export const CHURCH_KINDS = ['histórica', 'pentecostal', 'neopentecostal', 'católica'] as const
/** Rateio da receita do mês: parte da OSP e parte da ChildFund. */
export const REVENUE_SPLIT = { osp: 0.62, cfb: 0.38 }

const DAY_MS = 86_400_000
const dayMs = (s: string) => Date.parse(`${s.slice(0, 10)}T12:00:00Z`)
/** Dias de a até b (AAAA-MM-DD). */
export const daysBetween = (a: string, b: string) => Math.round((dayMs(b) - dayMs(a)) / DAY_MS)
export const monthsBetween = (a: string, b: string) => Math.max(0, Math.round(daysBetween(a, b) / 30.44))

const br = (s: string | null | undefined) => (s ? s.slice(0, 10).split('-').reverse().join('/') : '—')
const num = (n: number) => n.toLocaleString('pt-BR')
const brl = (n: number) => `R$ ${n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
const plural = (n: number, one: string, many: string) => `${num(n)} ${n === 1 ? one : many}`
const MONTHS = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez']
const monthLabel = (ym: string) => `${MONTHS[Number(ym.slice(5, 7)) - 1] ?? '?'}/${ym.slice(2, 4)}`
const place = (city: string | null, uf: string | null) => (city && uf ? `${city}/${uf}` : (city ?? uf ?? null))
/** Junta as partes que existem com " · ". */
const dot = (...parts: (string | null | undefined | false)[]) => parts.filter(Boolean).join(' · ')

const avg = (xs: number[]) => (xs.length ? xs.reduce((s, x) => s + x, 0) / xs.length : 0)
const r1 = (n: number) => Math.round(n * 10) / 10
const r2 = (n: number) => Math.round(n * 100) / 100

/** Monta os índices do grafo. */
export function createErpIndex(graph: ErpGraph): ErpIndex {
    const today = graph.today
    const byId = new Map<string, Entity>()
    for (const t of ENTITY_TYPES) for (const e of graph[t]) byId.set(`${t}:${e.id}`, e)
    const get = <T extends EntityType>(type: T, id: string | null | undefined) => (id ? (byId.get(`${type}:${id}`) as EntityOf<T> | undefined) : undefined)
    const all = <T extends EntityType>(type: T) => graph[type] as unknown as EntityOf<T>[]
    const nameOf = (type: EntityType, id: string | null | undefined) => get(type, id)?.name ?? null

    const donors = graph.doador
    const donorsFrom = (type: EntityType, id: string) => donors.filter(d => d.origin?.type === type && d.origin.id === id)
    const donorsOfEvent = (id: string) => donors.filter(d => d.links.evento === id)
    const donorsOfConsultor = (id: string) => donors.filter(d => d.links.consultor === id)
    const childrenOf = (ospId: string) => graph.crianca.filter(k => k.osp === ospId)
    const activeChildren = (d: Donor) => d.lines.filter(l => l.status === 'ativa' && l.child).map(l => get('crianca', l.child)).filter((k): k is Child => !!k)
    const donorsOfOsp = (ospId: string) => donors.filter(d => d.lines.some(l => l.status === 'ativa' && l.child && get('crianca', l.child)?.osp === ospId))

    const relations = (e: Entity): Relation[] => {
        const out: Relation[] = []
        const seen = new Set<string>()
        const r = (type: EntityType, id: string | null | undefined, rel: string) => {
            if (!id || !byId.has(`${type}:${id}`)) return
            const k = `${type}:${id}`
            if (k === `${e.type}:${e.id}` || seen.has(k)) return
            seen.add(k)
            out.push({ type, id, rel })
        }
        switch (e.type) {
            case 'doador': {
                if (e.origin) r(e.origin.type, e.origin.id, 'origem')
                r('evento', e.links.evento, 'inscrição')
                r('consultor', e.links.consultor, 'conduziu')
                r('igreja', e.links.igreja, 'frequenta')
                r('empresa', e.links.empresa, 'trabalha em')
                r('embaixador', e.links.embaixador, 'acompanha')
                e.lines.filter(l => l.status === 'ativa' && l.child).forEach(l => r('crianca', l.child, e.status === 'pre' ? 'reservada' : 'apadrinha'))
                e.lines.filter(l => l.status === 'ativa').forEach(l => r('osp', l.osp, 'projeto'))
                e.lines.filter(l => l.status !== 'ativa' && l.child).forEach(l => r('crianca', l.child, 'apadrinhou'))
                break
            }
            case 'igreja': {
                const evs = graph.evento.filter(x => x.hostType === 'igreja' && x.hostId === e.id)
                evs.forEach(x => r('evento', x.id, x.upcoming ? 'vai sediar' : 'sediou'))
                evs.forEach(x => x.consultores.forEach(c => r('consultor', c, 'conduziu')))
                r('osp', e.osp, 'projeto parceiro')
                donorsFrom('igreja', e.id).forEach(d => r('doador', d.id, 'veio daqui'))
                donors.filter(d => d.links.igreja === e.id).forEach(d => r('doador', d.id, 'frequenta'))
                break
            }
            case 'evento': {
                if (e.hostType) r(e.hostType, e.hostId, 'anfitriã')
                e.consultores.forEach(c => r('consultor', c, 'conduziu'))
                r('embaixador', e.amb, 'apresentou')
                donorsOfEvent(e.id).forEach(d => r('doador', d.id, 'ficha'))
                e.reserved.forEach(k => r('crianca', k, 'reservada'))
                break
            }
            case 'consultor': {
                const evs = graph.evento.filter(x => x.consultores.includes(e.id))
                evs.forEach(x => r('evento', x.id, x.upcoming ? 'vai conduzir' : 'conduziu'))
                evs.forEach(x => { if (x.hostType === 'igreja') r('igreja', x.hostId, 'atende') })
                break
            }
            case 'empresa': {
                r('osp', e.osp, 'projeto apoiado')
                graph.evento.filter(x => x.hostType === 'empresa' && x.hostId === e.id).forEach(x => r('evento', x.id, 'sediou'))
                donorsFrom('empresa', e.id).forEach(d => r('doador', d.id, 'veio daqui'))
                donors.filter(d => d.links.empresa === e.id).forEach(d => r('doador', d.id, 'trabalha aqui'))
                break
            }
            case 'embaixador': {
                graph.evento.filter(x => x.amb === e.id).forEach(x => r('evento', x.id, 'apresentou'))
                donorsFrom('embaixador', e.id).forEach(d => r('doador', d.id, 'veio daqui'))
                donors.filter(d => d.links.embaixador === e.id).forEach(d => r('doador', d.id, 'acompanha'))
                break
            }
            case 'osp': {
                graph.igreja.filter(x => x.osp === e.id).forEach(x => r('igreja', x.id, 'parceira'))
                graph.empresa.filter(x => x.osp === e.id).forEach(x => r('empresa', x.id, 'apoia'))
                donorsOfOsp(e.id).forEach(d => r('doador', d.id, 'padrinho daqui'))
                childrenOf(e.id).forEach(k => r('crianca', k.id, 'inscrita'))
                break
            }
            case 'crianca': {
                r('doador', e.sponsor, e.status === 'reservada' ? 'reservada para' : 'padrinho')
                r('osp', e.osp, 'projeto')
                r('evento', e.reservedFor, 'reservada')
                donors.filter(d => d.lines.some(l => l.child === e.id && l.status !== 'ativa')).forEach(d => r('doador', d.id, 'ex-padrinho'))
                break
            }
        }
        return out
    }

    const originText = (d: Donor) => (d.origin ? (nameOf(d.origin.type, d.origin.id) ?? CHANNEL_LABELS[d.channel]) : CHANNEL_LABELS[d.channel])
    const linesText = (d: Donor) => {
        const act = d.lines.filter(l => l.status === 'ativa')
        const kids = act.filter(l => l.child).length
        const guardian = act.some(l => l.product === 'Guardião da Infância')
        const parts: string[] = []
        if (kids) parts.push(plural(kids, 'criança', 'crianças'))
        if (guardian) parts.push('Guardião')
        return parts.length ? `${parts.join(' + ')} · ${brl(d.monthly)}/mês` : 'sem linhas ativas'
    }
    const ospPlace = (o: Osp) => place(o.place, o.uf)
    const canceledText = (d: Donor) => (d.canceledAt ? `Cancelado em ${br(d.canceledAt)}` : 'Cancelado')

    const metaOf = (e: Entity): string => {
        switch (e.type) {
            case 'doador':
                return e.status === 'cancelado'
                    ? dot(canceledText(e), place(e.city, e.uf))
                    : dot(DONOR_STATUS_LABELS[e.status], place(e.city, e.uf), graph.coverage.contracts && `${brl(e.monthly)}/mês`)
            case 'igreja': return dot(place(e.city, e.uf), e.kind) || 'Igreja'
            case 'evento': return dot(e.kind, br(e.date), e.upcoming ? 'próximo' : e.fichas != null && plural(e.fichas, 'ficha', 'fichas'))
            case 'consultor': return dot('Consultor de campo', e.region)
            case 'empresa': return dot(e.place, e.program) || 'Empresa'
            case 'embaixador': return dot(e.cat, e.reach) || 'Embaixador'
            case 'osp': return dot(ospPlace(e), e.enrolled != null && `${num(e.enrolled)} crianças`) || 'OSP'
            case 'crianca': {
                const o = get('osp', e.osp)
                return dot(e.age != null && `${e.age} anos`, CHILD_STATUS_LABELS[e.status], o && ospPlace(o))
            }
        }
    }

    const peekLines = (e: Entity): string[] => {
        const lines = (...xs: (string | null | undefined | false)[]) => xs.filter((x): x is string => !!x)
        switch (e.type) {
            case 'doador': {
                const first = e.status === 'pre' ? `Pré-ativo · cadastro em ${br(e.since)}` : e.status === 'cancelado' ? canceledText(e) : `${DONOR_STATUS_LABELS[e.status]} · desde ${br(e.since)}`
                const via = e.origin ? `Veio por ${originText(e)}` : `Veio pelo canal ${CHANNEL_LABELS[e.channel]}`
                const third = e.status === 'cancelado' ? (e.cancelReason ? `Motivo: ${e.cancelReason}` : null) : graph.coverage.contracts ? linesText(e) : null
                return lines(first, via, third)
            }
            case 'igreja': {
                const n = donorsFrom('igreja', e.id).length
                const visit = e.lastVisit == null ? 'nenhuma visita registrada' : `última visita há ${plural(e.lastVisit, 'dia', 'dias')}`
                return lines(dot(place(e.city, e.uf), e.kind), e.pastor, `${plural(n, 'doador', 'doadores')} · ${visit}`)
            }
            case 'evento': {
                const host = e.hostType ? nameOf(e.hostType, e.hostId) : e.amb ? nameOf('embaixador', e.amb) : null
                const third = e.upcoming
                    ? `Previsto · ${plural(e.reserved.length, 'criança reservada', 'crianças reservadas')}`
                    : dot(e.audience != null && `${num(e.audience)} pessoas`, e.fichas != null && plural(e.fichas, 'ficha', 'fichas'))
                return lines(dot(e.kind, br(e.date)), host && `Em ${host}`, third)
            }
            case 'consultor': {
                const evs = graph.evento.filter(x => x.consultores.includes(e.id)).length
                return lines(dot('Consultor de campo', e.region), `${plural(evs, 'evento', 'eventos')} · ${plural(donorsOfConsultor(e.id).length, 'doador', 'doadores')}`, 'Contratado por TDR')
            }
            case 'empresa':
                return lines(e.place, e.program, dot(e.staff != null && `${num(e.staff)} colaboradores`, plural(donorsFrom('empresa', e.id).length, 'doador', 'doadores')))
            case 'embaixador':
                return lines(e.cat, e.reach, plural(donorsFrom('embaixador', e.id).length, 'doador trazido', 'doadores trazidos'))
            case 'osp':
                return lines(
                    dot(ospPlace(e), e.focus),
                    dot(e.enrolled != null && `${num(e.enrolled)} crianças inscritas`, e.educators != null && `${e.educators} educadores`),
                    e.subsidy && `Subsídio ${monthLabel(e.subsidy.month)}: ${e.subsidy.status}`,
                )
            case 'crianca': {
                const st = e.status === 'apadrinhada'
                    ? `Apadrinhada por ${nameOf('doador', e.sponsor) ?? 'doador'}`
                    : e.status === 'reservada'
                        ? e.reservedFor ? `Reservada para ${nameOf('evento', e.reservedFor) ?? 'evento'}` : `Reservada para ${nameOf('doador', e.sponsor) ?? 'doador'}`
                        : e.daysWithout != null ? `${CHILD_STATUS_LABELS[e.status]} · ${e.daysWithout} dias sem padrinho` : CHILD_STATUS_LABELS[e.status]
                return lines(dot(e.age != null && `${e.age} anos`, nameOf('osp', e.osp)), st)
            }
        }
    }

    // ---------- indicadores ----------
    const monthsOf = (d: Donor) => monthsBetween(d.since, d.canceledAt || today)
    const groupStats = (ds: Donor[]) => {
        const total = ds.length
        const canceled = ds.filter(d => d.status === 'cancelado').length
        const live = ds.filter(d => d.status !== 'cancelado')
        return {
            total, active: total - canceled, canceled,
            retention: total ? r1(((total - canceled) / total) * 100) : 0,
            avgTicket: r2(avg(live.map(d => d.monthly))),
            avgMonths: r1(avg(ds.map(monthsOf))),
        }
    }
    /** Doadores que contam nos indicadores: sem lead e sem pré-ativo (ainda não contribuíram). */
    const counted = (d: Donor) => d.status !== 'pre' && d.status !== 'lead'

    const channelStats = (): ChannelStat[] => CHANNELS.map(channel => {
        const ds = donors.filter(d => d.channel === channel && counted(d))
        return { channel, ...groupStats(ds), guardians: ds.filter(d => d.roles.includes('guardiao')).length }
    })
    const churchKindStats = (): ChurchKindStat[] => CHURCH_KINDS.map(kind => {
        const churches = graph.igreja.filter(i => i.kind === kind).length
        const ds = donors.filter(d => counted(d) && d.origin?.type === 'igreja' && get('igreja', d.origin.id)?.kind === kind)
        const g = groupStats(ds)
        return { kind, churches, donors: g.total, canceled: g.canceled, retention: g.retention, avgTicket: g.avgTicket, avgMonths: g.avgMonths }
    })
    const overdue = (): Overdue[] => donors.flatMap((d): Overdue[] => {
        const late = d.invoices.filter(i => i.status === 'em atraso')
        const first = late[0]
        if (!first) return []
        return [{ donor: d, months: late.length, amount: late.reduce((s, i) => s + i.value, 0), reason: late[late.length - 1]?.reason ?? '', since: first.due, days: daysBetween(first.due, today) }]
    }).sort((a, b) => b.since.localeCompare(a.since))
    const exits = (): Exit[] => donors.filter(d => d.status === 'cancelado').map((d): Exit => {
        // sem data de cancelamento (live), a etapa conta até hoje e a data fica vazia
        const days = daysBetween(d.since, d.canceledAt ?? today)
        return { donor: d, origin: originText(d), stage: days <= 90 ? 'onboarding (90 dias)' : days <= 365 ? '1º ano' : 'após 1 ano', months: monthsOf(d), reason: d.cancelReason ?? '', date: d.canceledAt ?? '' }
    }).sort((a, b) => b.date.localeCompare(a.date))
    const near180 = () => graph.crianca.filter(k => k.status === 'disponível' && k.daysWithout != null).sort((a, b) => (b.daysWithout ?? 0) - (a.daysWithout ?? 0))
    const lettersDue = () => donors.flatMap(d => d.letters.filter(l => l.status === 'vencendo' || l.status === 'atrasada').map(letter => ({ donor: d, letter })))
        .sort((a, b) => a.letter.due.localeCompare(b.letter.due))

    /** Receita do mês por OSP (faturas não em atraso: pagas, reprocessadas e abertas a vencer), rateio 62/38. Guardião vai à OSP da linha. */
    const revenueByOsp = (month = today.slice(0, 7)): OspRevenue[] => {
        const acc = new Map<string, { osp: Osp; gross: number; donors: Set<string> }>()
        for (const o of graph.osp) acc.set(o.id, { osp: o, gross: 0, donors: new Set() })
        for (const d of donors) for (const i of d.invoices) {
            if (i.month !== month || i.status === 'em atraso') continue
            for (const l of d.lines) {
                if (l.convertedAt > i.due || (l.endedAt && l.endedAt <= i.due)) continue
                const a = l.osp ? acc.get(l.osp) : undefined
                if (!a) continue
                a.gross += l.value
                a.donors.add(d.id)
            }
        }
        return [...acc.values()]
            .map(a => ({ osp: a.osp, gross: a.gross, ospShare: r2(a.gross * REVENUE_SPLIT.osp), cfbShare: r2(a.gross * REVENUE_SPLIT.cfb), donors: a.donors.size }))
            .sort((a, b) => b.gross - a.gross)
    }
    const monthSummary = (month = today.slice(0, 7)): MonthSummary => {
        const inv = donors.flatMap(d => d.invoices.filter(i => i.month === month))
        const isPaid = (i: (typeof inv)[number]) => i.status === 'paga' || i.status === 'negada → reprocessada'
        const sum = (xs: typeof inv) => xs.reduce((s, i) => s + i.value, 0)
        const byMethod: MonthSummary['byMethod'] = {}
        for (const i of inv) {
            const m = (byMethod[i.method] ??= { count: 0, paid: 0, amount: 0 })
            m.count++
            m.amount += i.value
            if (isPaid(i)) m.paid++
        }
        const late = inv.filter(i => i.status === 'em atraso')
        return {
            month,
            billed: inv.length, paid: inv.filter(isPaid).length, denied: inv.filter(i => i.status === 'negada → reprocessada').length,
            overdue: late.length, open: inv.filter(i => i.status === 'aberta').length,
            billedAmount: sum(inv), paidAmount: sum(inv.filter(isPaid)), overdueAmount: sum(late),
            byMethod,
        }
    }
    const countByStatus = () => {
        const out = Object.fromEntries(DONOR_STATUSES.map(s => [s, 0])) as Record<DonorStatus, number>
        for (const d of donors) out[d.status]++
        return out
    }

    return {
        graph, get, all, relations, metaOf, peekLines,
        donorsFrom, donorsOfEvent, donorsOfConsultor, donorsOfOsp, childrenOf, activeChildren,
        channelStats, churchKindStats, overdue, exits, near180, lettersDue, revenueByOsp, monthSummary, countByStatus,
    }
}
